import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type {
  Counterparty,
  CounterpartySource,
  CounterpartyType,
  ListResponse,
} from './types'

const ROOT = '/counterparties'
const KEY = ['counterparties'] as const

export function useCounterparties(filter: {
  type?: CounterpartyType
  source?: CounterpartySource
  search?: string
} = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, filter],
    queryFn: () =>
      api<ListResponse<Counterparty>>(
        `${ROOT}${buildQuery({ type: filter.type, source: filter.source, search: filter.search })}`,
      ).then((r) => r.items),
  })
}

export type CreateCounterpartyInput = {
  name: string
  type: CounterpartyType
  taxId?: string | null
  notes?: string | null
}

export function useCreateCounterparty() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCounterpartyInput) =>
      api<Counterparty>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type UpdateCounterpartyInput = {
  id: string
  patch: Partial<{
    name: string
    type: CounterpartyType
    taxId: string | null
    notes: string | null
    defaultPaymentAccountId: string | null
  }>
}

export function useUpdateCounterparty() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateCounterpartyInput) =>
      api<Counterparty>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteCounterparty() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
